/**
 * What a new account sees before it can run a session.
 *
 * There is no waitlist and nothing to be admitted to — you sign in and land
 * here. The only requirement is that somebody is covering the API cost of a
 * session, which is either:
 *
 *   - the trial, taken automatically — a new account never sees a choice,
 *   - a guest pass, for someone who was sent a code, or
 *   - a subscription.
 *
 * The trial fires on mount with no button. The button version failed a live
 * test: its copy said "a pass goes to your email", so a real user sat waiting
 * for an email that does not exist, with a pass-code field right below
 * completing the wrong story. The options screen now only appears when the
 * trial CANNOT fire — already used, daily limit, or a lapsed subscription —
 * and says why.
 */
import { useEffect, useRef, useState } from 'react';

import {
  openBillingPortal,
  redeemInvite,
  startCheckout,
  startTrial,
  userFacingError,
} from '@/api/client';
import type { BillingState } from '@/api/types';

const GUEST_PASS_EMAIL = 'teddyshachtman@gmail.com';

interface Props {
  billing: BillingState | null;
  /** Shown so the pass's binding is concrete: this address, not "your
   *  account" in the abstract. */
  email: string;
  onRedeemed: () => void;
}

export function GetAccess({ billing, email, onRedeemed }: Props) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorTone, setErrorTone] = useState<'gentle' | 'error'>('error');

  // "past_due" and "canceled" mean they had access and lost it, which needs
  // the billing portal rather than a fresh signup.
  const lapsed = Boolean(
    billing && billing.subscription_status !== 'inactive',
  );

  // Auto-take the trial for a brand-new account. 'granting' renders a quiet
  // holding card; success refreshes billing and this screen unmounts without
  // the user ever seeing options. Failure drops to the options screen with
  // the server's reason (already used / daily limit), which are the only
  // cases where there is a real decision to make.
  const [granting, setGranting] = useState(!lapsed);
  // Why the options screen is showing, distinct from `error` so a later
  // interactive failure (bad code, checkout down) doesn't stack two notices.
  const [reason, setReason] = useState<string | null>(null);
  const attempted = useRef(false);
  useEffect(() => {
    if (lapsed || attempted.current) return;
    // The ref, not state, guards the double-invoke: two POSTs would race and
    // the loser's 409 would overwrite the winner's success with an error.
    attempted.current = true;
    void (async () => {
      try {
        await startTrial();
        onRedeemed();
      } catch (err) {
        setReason(
          userFacingError(
            err,
            'The free trial isn’t available right now. Choose another option below.',
          ),
        );
        setGranting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function redeem(): Promise<void> {
    const trimmed = code.trim();
    if (!trimmed) return;
    setBusy('redeem');
    setError(null);
    setErrorTone('gentle');
    try {
      await redeemInvite(trimmed);
      onRedeemed();
    } catch (err) {
      setError(userFacingError(err, 'That pass code didn’t work. Check it and try again.'));
      setBusy(null);
    }
  }

  async function checkout(plan: 'alpha' | 'trial'): Promise<void> {
    setBusy(plan);
    setError(null);
    setErrorTone('error');
    try {
      const { url } = await startCheckout(plan);
      window.location.href = url;
    } catch (err) {
      setError(
        userFacingError(
          err,
          'Checkout is temporarily unavailable. Please try again later.',
        ),
      );
      setBusy(null);
    }
  }

  async function portal(): Promise<void> {
    setBusy('portal');
    setError(null);
    setErrorTone('error');
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (err) {
      setError(
        userFacingError(
          err,
          'The billing portal is temporarily unavailable. Please try again later.',
        ),
      );
      setBusy(null);
    }
  }

  if (granting) {
    return (
      <div className="access">
        <div className="gate__card">
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <span className="spinner" aria-hidden="true" />
            <p style={{ margin: 0 }}>Setting up your free sessions…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="access">
      <header>
        <h1 className="access__title">
          {lapsed ? 'Your subscription needs attention' : 'Pick how to keep playing'}
        </h1>
        <p className="access__lede">
          {lapsed
            ? 'Sessions are paused until billing is sorted out. Your campaigns and canon are untouched.'
            : reason ?? 'The free trial is not available for this account.'}
        </p>
      </header>

      {lapsed ? (
        <section className="card">
          <button
            type="button"
            className="button button--primary"
            onClick={() => void portal()}
            disabled={busy !== null}
          >
            {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
          </button>
        </section>
      ) : (
        <div className="access__paths">
          <section className="card access__path">
            <span className="badge badge--ok">No payment</span>
            <h2 className="card__title">I have a pass code</h2>
            <p className="card__body">
              A pass covers your sessions on{' '}
              {email ? <strong>{email}</strong> : 'this account'} — no card,
              nothing to cancel.
            </p>
            <label className="field">
              <span className="field__label">Pass code</span>
              <input
                className="field__input"
                value={code}
                placeholder="TL-XXXXXX-XXXXXX-XXXXXX"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void redeem();
                }}
              />
            </label>
            <button
              type="button"
              className="button"
              onClick={() => void redeem()}
              disabled={busy !== null || !code.trim()}
            >
              {busy === 'redeem' ? 'Redeeming…' : 'Redeem pass'}
            </button>
          </section>

          <section className="card access__path">
            <h2 className="card__title">Or pay for what you use</h2>
            <p className="card__body">
              When the trial runs out, a monthly plan keeps you going. You are
              never billed past the plan price, whatever happens in a session.
            </p>
            <div className="access__plans">
              <button
                type="button"
                className="button button--primary"
                onClick={() => void checkout('alpha')}
                disabled={busy !== null}
              >
                {busy === 'alpha' ? 'Opening…' : '$13 / month'}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => void checkout('trial')}
                disabled={busy !== null}
              >
                {busy === 'trial' ? 'Opening…' : 'Try it — $4.50'}
              </button>
            </div>
            <p className="card__note">
              $13 covers about six four-hour sessions a month; $4.50 covers
              about two. Cancel any time.{' '}
              <a href={`mailto:${GUEST_PASS_EMAIL}?subject=Throughline`}>
                Need more?
              </a>
            </p>
          </section>
        </div>
      )}

      {error ? (
        <div
          className={
            errorTone === 'gentle' ? 'notice notice--gentle' : 'notice notice--error'
          }
          role={errorTone === 'gentle' ? 'status' : 'alert'}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
